import { ComponentDesign, Member, PlatePair } from '@paragon/contract-library';

import type { RootState } from '../store';
import { getActiveComponentDesign } from '../store/selectors/getActiveComponentDesignSelector';
import { getSelectedItemEntries } from '../store/selectors/getSelectedItemSelector';
import { describeSelectedItem } from './snapshot';
import { describeLumber, describeMemberGeometry, getTrussMembers } from './tools/helpers';
import { describeTrussEnvelope, describeWall } from './tools/layoutDescriptions';
import { describeProfileGeometry } from './tools/profileGeometry';

/**
 * Records what a designer does in the app while showing the Assistant how, as steps a person or a model can read:
 * "W4's lumber changed from 2x4 SP No.3 to 2x4 SP No.2", not the app's internal actions. Each step comes from
 * comparing the project before and after the app settles, so a drag is one step, not hundreds.
 */

export interface RecordedStep {
  /** Milliseconds since the recording started. */
  readonly at: number;
  readonly text: string;
}

export interface Recording {
  readonly startedAt: string;
  readonly steps: readonly RecordedStep[];
  /** What the designer typed while recording, to say why. */
  readonly notes: readonly RecordedStep[];
}

const round = (value: number) => Math.round(value * 16) / 16;
const point = ({ x, y }: { x: number; y: number }) => `(${round(x)}, ${round(y)})`;

function describeCenterline(member: Member, componentDesign: ComponentDesign) {
  const { centerline } = describeMemberGeometry(member, componentDesign.component);
  return centerline == null ? '' : ` from ${point(centerline.from)} to ${point(centerline.to)}`;
}

const describePlate = (plate: PlatePair) =>
  `plate ${plate.name} (${plate.width}x${plate.length} at ${point({ x: plate.centerX, y: plate.centerY })}, ` +
  `${round(plate.angleZ)}°)`;

function describeProfileSafely(componentDesign: ComponentDesign) {
  try {
    if (componentDesign.profile == null) return null;
    const { outline, leftOverhang, rightOverhang, heelHeights, topChordSlopes } = describeProfileGeometry(
      componentDesign.profile,
    );
    return { outline, leftOverhang, rightOverhang, heelHeights, topChordSlopes };
  } catch {
    return null;
  }
}

/** What changed on one truss, member by member, matching members by identity since edits renumber them. */
export function describeTrussChange(before: ComponentDesign | undefined, after: ComponentDesign | undefined) {
  if (before == null && after == null) return [];
  if (before == null) return [`Created truss ${after!.component.name} (span ${round(after!.component.span)})`];
  if (after == null) return [`Deleted truss ${before.component.name}`];
  if (before.component === after.component && before.profile === after.profile) return [];

  const truss = after.component.name;
  const steps: string[] = [];
  if (before.component.name !== truss) steps.push(`Renamed truss ${before.component.name} to ${truss}`);

  const beforeProfile = describeProfileSafely(before);
  const afterProfile = describeProfileSafely(after);
  if (beforeProfile != null && afterProfile != null)
    for (const key of Object.keys(afterProfile) as (keyof typeof afterProfile)[]) {
      const was = JSON.stringify(beforeProfile[key]);
      const now = JSON.stringify(afterProfile[key]);
      if (was !== now) steps.push(`${truss}: profile ${key} changed from ${was} to ${now}`);
    }

  const beforeMembers = new Map(getTrussMembers(before).map((member) => [member.guid, member]));
  const afterMembers = new Map(getTrussMembers(after).map((member) => [member.guid, member]));
  for (const [guid, member] of afterMembers) {
    const was = beforeMembers.get(guid);
    if (was == null) {
      steps.push(
        `${truss}: added ${member.memberType} ${member.name}${describeCenterline(member, after)}, ` +
          describeLumber(member),
      );
      continue;
    }
    if (describeLumber(was) !== describeLumber(member))
      steps.push(`${truss}: ${member.name}'s lumber changed from ${describeLumber(was)} to ${describeLumber(member)}`);
    const wasLine = describeCenterline(was, before);
    const nowLine = describeCenterline(member, after);
    if (wasLine !== nowLine && wasLine !== '' && nowLine !== '')
      steps.push(
        `${truss}: ${member.name} moved:${wasLine.replace(' from', '')} became${nowLine.replace(' from', '')}`,
      );
  }
  for (const [guid, member] of beforeMembers)
    if (!afterMembers.has(guid))
      steps.push(`${truss}: deleted ${member.memberType} ${member.name}${describeCenterline(member, before)}`);

  const beforePlates = new Map(before.component.platePairs.map((plate) => [plate.guid, plate]));
  const afterPlates = new Map(after.component.platePairs.map((plate) => [plate.guid, plate]));
  // Upgrade replaces every plate at once; say so instead of listing each.
  const replacedPlates = [...afterPlates.keys()].filter((guid) => !beforePlates.has(guid)).length;
  if (replacedPlates > 3) steps.push(`${truss}: replated (${replacedPlates} new plates)`);
  else {
    for (const [guid, plate] of afterPlates) {
      const was = beforePlates.get(guid);
      if (was == null) steps.push(`${truss}: added ${describePlate(plate)}`);
      else if (describePlate(was) !== describePlate(plate))
        steps.push(`${truss}: ${describePlate(was)} became ${describePlate(plate)}`);
    }
    for (const [guid, plate] of beforePlates)
      if (!afterPlates.has(guid)) steps.push(`${truss}: deleted ${describePlate(plate)}`);
  }

  const bearingNames = (design: ComponentDesign) => design.component.bearings.map((bearing) => bearing.name).join(', ');
  if (bearingNames(before) !== bearingNames(after))
    steps.push(`${truss}: bearings changed from [${bearingNames(before)}] to [${bearingNames(after)}]`);

  const spliceSpots = (design: ComponentDesign) =>
    design.component.chordSplices.map((splice) => `${splice.name} at x ${round(splice.centerPointX)}`).join(', ');
  if (spliceSpots(before) !== spliceSpots(after))
    steps.push(`${truss}: splices changed from [${spliceSpots(before)}] to [${spliceSpots(after)}]`);

  return steps;
}

function describeLayoutChange(before: RootState, after: RootState) {
  const steps: string[] = [];
  const compare = <T extends { guid: string }>(
    what: string,
    was: Record<string, T>,
    now: Record<string, T>,
    describe: (item: T) => unknown,
  ) => {
    if (was === now) return;
    for (const [guid, item] of Object.entries(now)) {
      const old = was[guid];
      if (old == null) steps.push(`Added ${what} ${JSON.stringify(describe(item))}`);
      else if (old !== item && JSON.stringify(describe(old)) !== JSON.stringify(describe(item)))
        steps.push(`Changed ${what} ${JSON.stringify(describe(old))} to ${JSON.stringify(describe(item))}`);
    }
    for (const [guid, item] of Object.entries(was))
      if (now[guid] == null) steps.push(`Deleted ${what} ${JSON.stringify(describe(item))}`);
  };
  compare('wall', before.layout.bearingEnvelopes, after.layout.bearingEnvelopes, describeWall);
  compare('truss envelope', before.layout.trussEnvelopes, after.layout.trussEnvelopes, (envelope) =>
    describeTrussEnvelope(after, envelope),
  );
  compare('roof plane', before.layout.roofPlanes, after.layout.roofPlanes, (plane) => ({
    guid: plane.guid.slice(0, 8),
  }));
  return steps;
}

/** Which items are selected, by identity, so a selected member's lumber changing isn't a new selection. */
function selectionKey(state: RootState) {
  return getSelectedItemEntries(state)
    .map((entry) => `${entry.type}:${(entry.item as { guid?: string } | null)?.guid ?? ''}`)
    .join(',');
}

/** The selection in a few words, such as "member W4 from (168, 3.5) to (168, 135.8125)". */
function describeSelection(state: RootState) {
  const component = getActiveComponentDesign(state)?.component ?? null;
  return getSelectedItemEntries(state)
    .map((entry) => {
      const item = describeSelectedItem(entry, component, state) as {
        type: string;
        name?: string;
        centerline?: { from: { x: number; y: number }; to: { x: number; y: number } } | null;
        at?: { x: number; y: number };
      };
      const where =
        item.centerline != null
          ? ` from ${point(item.centerline.from)} to ${point(item.centerline.to)}`
          : item.at != null
            ? ` at ${point(item.at)}`
            : '';
      return `${item.type}${item.name != null ? ` ${item.name}` : ''}${where}`;
    })
    .join('; ');
}

/** Where the designer is and what tool they have open, which says how they did what the diffs show. */
function describeContext(state: RootState) {
  return {
    openTruss: getActiveComponentDesign(state)?.component.name ?? null,
    panel: state.panelData.trussEditsPanel,
    inserting: state.application.insertingObjectInfo?.type ?? null,
    editing: state.editor.editInProgress || null,
  };
}

/** Everything the designer changed between two settled states, in the order a person would tell it. */
export function describeStateChange(before: RootState, after: RootState): string[] {
  const steps: string[] = [];

  const wasContext = describeContext(before);
  const nowContext = describeContext(after);
  if (wasContext.openTruss !== nowContext.openTruss)
    steps.push(nowContext.openTruss == null ? 'Left the truss editor' : `Opened truss ${nowContext.openTruss}`);
  if (wasContext.panel !== nowContext.panel && nowContext.panel != null)
    steps.push(`Opened the ${nowContext.panel} panel`);
  if (wasContext.inserting !== nowContext.inserting && nowContext.inserting != null)
    steps.push(`Started inserting a ${nowContext.inserting}`);
  if (wasContext.editing !== nowContext.editing && nowContext.editing != null)
    steps.push(`Started ${nowContext.editing}`);

  if (before.componentDesigns.data !== after.componentDesigns.data) {
    const guids = new Set([...Object.keys(before.componentDesigns.data), ...Object.keys(after.componentDesigns.data)]);
    for (const guid of guids)
      steps.push(...describeTrussChange(before.componentDesigns.data[guid], after.componentDesigns.data[guid]));
  }
  if (before.componentDesignTimestamps !== after.componentDesignTimestamps)
    for (const [guid, timestamps] of Object.entries(after.componentDesignTimestamps)) {
      const was = before.componentDesignTimestamps[guid];
      if (timestamps?.lastAnalyzed != null && timestamps.lastAnalyzed !== was?.lastAnalyzed) {
        const name = after.componentDesigns.data[guid]?.component.name ?? guid.slice(0, 8);
        steps.push(`Analyzed ${name}: capacity ratio ${timestamps.lastAnalyzedCapacity ?? 'unknown'}`);
      }
    }
  if (before.layout !== after.layout) steps.push(...describeLayoutChange(before, after));

  if (selectionKey(before) !== selectionKey(after)) {
    const selection = describeSelection(after);
    if (selection !== '') steps.push(`Selected ${selection}`);
  }

  return steps;
}

/** How long the app must be still before a change counts as one step. */
const SETTLE_MS = 600;
/** Recordings past this are cut off; the designer is shown that it's full. */
export const MAX_RECORDED_STEPS = 300;

/**
 * Starts recording the designer's changes to the app's state. `onStep` gets each step as it's recorded; `isPaused`
 * skips changes the Assistant itself makes while it runs. Returns a function that stops recording and flushes the
 * last change.
 */
export function startRecording(
  store: { getState: () => RootState; subscribe: (listener: () => void) => () => void },
  onStep: (step: RecordedStep) => void,
  isPaused: () => boolean,
  describe: (before: RootState, after: RootState) => string[] = describeStateChange,
) {
  const startedAt = Date.now();
  let settled = store.getState();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let recorded = 0;

  const flush = () => {
    timer = null;
    const now = store.getState();
    if (now === settled) return;
    const steps = isPaused() ? [] : describe(settled, now);
    settled = now;
    for (const text of steps) {
      if (recorded >= MAX_RECORDED_STEPS) return;
      recorded++;
      onStep({ at: Date.now() - startedAt, text });
    }
  };

  const unsubscribe = store.subscribe(() => {
    if (timer != null) clearTimeout(timer);
    timer = setTimeout(flush, SETTLE_MS);
  });

  return () => {
    unsubscribe();
    if (timer != null) clearTimeout(timer);
    flush();
  };
}
